import { useMemo } from 'react';

import { type HomeProductCategory } from '../useHomeContent';
import { useHomeMenu } from '../useHomeMenu';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Removes products from the filterable and generic categories that are not in the products array.
 * If there are no products, returns only the generic categories.
 */
export const useHomeFiltering = (params: UseHomeFilteringParams): UseHomeFilteringResult => {
  const { products = [], filterableCategories, genericCategories, caloriesOverrides } = params;

  return useMemo(() => {
    if (products.length === 0) {
      return {
        filterableCategories: [],
        genericCategories,
      };
    }

    const menuProductsByLineageKey = getMenuProductsByLineageKey(products);
    const caloriesOverridesBySlug = getCaloriesOverridesBySlug(caloriesOverrides);

    return {
      filterableCategories: filterCategories(
        filterableCategories,
        menuProductsByLineageKey,
        caloriesOverridesBySlug,
      ),
      genericCategories: filterCategories(
        genericCategories,
        menuProductsByLineageKey,
        caloriesOverridesBySlug,
      ),
    };
  }, [products, filterableCategories, genericCategories, caloriesOverrides]);
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Removes categories that have no products.
 */
function filterCategories(
  categories: HomeProductCategory[],
  menuProductsByLineageKey: MenuProductsByLineageKey,
  caloriesOverrides: CaloriesOverrides,
): HomeProductCategory[] {
  return categories
    .map((category) =>
      filterCategoryProducts(category, menuProductsByLineageKey, caloriesOverrides),
    )
    .filter((category) => (category.products?.length ?? 0) > 0);
}

/**
 * Removes products that are not in the selected menu and enriches them with the menu data.
 */
function filterCategoryProducts(
  category: HomeProductCategory,
  menuProductsByLineageKey: MenuProductsByLineageKey,
  caloriesOverrides: CaloriesOverrides,
): HomeProductCategory {
  return {
    ...category,
    products: category.products
      ?.filter(
        (product) => Boolean(product.deepLink) || menuProductsByLineageKey.has(product.lineageKey),
      )
      ?.map((product) => {
        if (product.deepLink) {
          return product;
        }

        const menuProduct = menuProductsByLineageKey.get(product.lineageKey)!;

        return {
          ...product,
          ...menuProduct,
          ...getCaloriesOverride(menuProduct, caloriesOverrides),
          isEnriched: true,
        } as FilteredMenuProduct;
      }),
  };
}

/**
 * Map of products by lineage key, to be provided in the Contentful content.
 */
function getMenuProductsByLineageKey(products: NonNullable<UseHomeFilteringParams['products']>) {
  return new Map(
    products
      .filter((product): product is MenuProductWithLineageKey => Boolean(product.lineageKey))
      .map((product) => [product.lineageKey, product]),
  );
}

/**
 * Map of calorie overrides by base product slug, from menu Contentful content.
 */
function getCaloriesOverridesBySlug(
  caloriesOverrides: UseHomeFilteringParams['caloriesOverrides'],
): CaloriesOverrides {
  return new Map(Object.entries(caloriesOverrides ?? {}));
}

function getCaloriesOverride(
  menuProduct: MenuProductWithLineageKey,
  caloriesOverrides: CaloriesOverrides,
) {
  const caloriesOverride = caloriesOverrides.get(menuProduct?.baseProductSlug);
  const calories = caloriesOverride ? caloriesOverride?.caloriesOverride : menuProduct?.calories;

  return {
    calories,
    calorieOverrideText: caloriesOverride?.caloriesOverrideText,
  };
}

// ─── Types ───────────────────────────────────────────────────────────────────

type MenuProduct = NonNullable<ReturnType<typeof useHomeMenu>['products']>[number];

type MenuProductWithLineageKey = MenuProduct & { slug: string; lineageKey: string };

type MenuProductsByLineageKey = Map<string, MenuProductWithLineageKey>;

type FilteredMenuProduct = HomeProductCategory['products'][number];

type CaloriesOverrideData = {
  caloriesOverride?: number;
  caloriesOverrideText?: string;
};

type CaloriesOverrides = Map<string, CaloriesOverrideData>;

type UseHomeFilteringParams = {
  products: ReturnType<typeof useHomeMenu>['products'];
  filterableCategories: HomeProductCategory[];
  genericCategories: HomeProductCategory[];
  caloriesOverrides?: Record<string, CaloriesOverrideData>;
};

type UseHomeFilteringResult = {
  filterableCategories: HomeProductCategory[];
  genericCategories: HomeProductCategory[];
};
